/**
 * RESPONSIBILITY: annotation metadata → JSON Schema. The DATA SHAPE, nothing about
 * how it is presented — that is ui-schema.ts.
 */
import { type FieldMeta, type SectionMeta, rowFields } from '../decorators';
import type { JsonSchema } from './types';

function fieldToJsonSchema(f: FieldMeta): JsonSchema {
  const base: JsonSchema = { type: 'string', title: f.label };
  if (f.description) base.description = f.description;
  if (f.default !== undefined) base.default = f.default;

  switch (f.kind) {
    case 'text':
    case 'paragraph':
      if (f.maxLength) base.maxLength = f.maxLength;
      if (f.minLength) base.minLength = f.minLength;
      return base;

    case 'color':
      return { ...base, format: 'color' };

    // A reference and the words that go with it here. Everything else about the file is
    // in the `images` table, looked up by path — copying it in is how two copies disagree.
    case 'img':
    case 'video':
      return {
        type: 'object',
        title: f.label,
        properties: {
          path: { type: 'string', title: 'Path', format: 'uri-reference' },
          alt: { type: 'string', title: 'Alt text' },
        },
      };

    // uri-reference, not uri: a link on a site is usually relative — "#contact",
    // "/events" — and `uri` demands a scheme, so every internal link an editor typed
    // came back "must match format uri" while being perfectly correct.
    case 'link':
      return { ...base, format: 'uri-reference' };

    // A plain string here; that it parses is enforced by Zod, which can run the parser.
    case 'repeat':
      return base;

    case 'date':
      return { ...base, format: f.time ? 'date-time' : 'date' };

    case 'num': {
      const n: JsonSchema = { ...base, type: f.integer ? 'integer' : 'number' };
      if (f.min !== undefined) n.minimum = f.min;
      if (f.max !== undefined) n.maximum = f.max;
      return n;
    }

    case 'bool':
      return { ...base, type: 'boolean' };

    // A vocabulary that lives in the document cannot be an enum here: the schema is
    // compiled once from the annotations, and `in` changes whenever an admin edits it.
    // Membership is enforced on write instead — see core/write-pipeline.ts.
    case 'choice':
      if (f.multiple) return { type: 'array', title: f.label, items: { type: 'string' } };
      return f.in ? base : { ...base, enum: f.options };

    case 'stringlist':
      return { type: 'array', title: f.label, items: { type: 'string' }, maxItems: f.maxItems };

    case 'list': {
      const itemFields = f.of ? rowFields(f.of()) : [];
      const arr: JsonSchema = {
        type: 'array',
        title: f.label,
        items: objectSchema(itemFields),
      };
      if (f.minItems !== undefined) arr.minItems = f.minItems;
      if (f.maxItems !== undefined) arr.maxItems = f.maxItems;
      return arr;
    }
  }
}

function objectSchema(fields: FieldMeta[]): JsonSchema {
  const properties: Record<string, JsonSchema> = {};
  const required: string[] = [];
  for (const f of fields) {
    properties[f.key] = fieldToJsonSchema(f);
    if (f.required) required.push(f.key);
  }
  const s: JsonSchema = { type: 'object', properties };
  if (required.length) s.required = required;
  return s;
}

export const toJsonSchema = (section: SectionMeta): JsonSchema => objectSchema(section.fields);
